import { useState } from "react";
import { Person, CheckCircle } from "@material-ui/icons";
import { Checkbox, FormControlLabel, Typography } from "@material-ui/core";
import DealTransferDialogStyles from "./DealTransferDialogStyles";
import Utils from "../../../../../helpers/Utils";
import { dealTransfer } from "../../../../../api/contactApi";

const DealTransferDialog = ({ open, onClose, leadOwnerDetails, subUserList, openDeals, dealTransferCallback }) => {
  const usersObj = Utils.convertArrayToIdObject(subUserList);
  const classes = DealTransferDialogStyles();
  const [selectedDeals, setSelectedDeals] = useState([]);
  const [isDealTransfering, setIsDealTransferring] = useState(false);

  const handleSelectAll = () => {
    if (openDeals.length > 0 && selectedDeals.length === openDeals.length) {
      setSelectedDeals([]);
    } else {
      setSelectedDeals(openDeals.map((deal) => deal.contact_stage_id));
    }
  };

  const handleSelectDeal = (dealId) => {
    if (selectedDeals.includes(dealId)) {
      setSelectedDeals((prev) => prev.filter((id) => id !== dealId));
    } else {
      setSelectedDeals((prev) => [...prev, dealId]);
    }
  };

  const handleTransfer = async () => {
    if (selectedDeals.length < 1) return;
    setIsDealTransferring(true);
    try {
      const res = await dealTransfer({
        contact_id: leadOwnerDetails?.contactId,
        new_owner_id: leadOwnerDetails?.newOwnerId,
        contact_stage_ids: selectedDeals,
      });

      if (res.success) {
        dealTransferCallback();
        window.showNotification("SUCCESS", res.message);
        onClose();
      } else {
        window.showNotification("ERROR", res.message);
      }
    } catch (e) {
      console.log(e);
      window.showNotification("ERROR", "Something went wrong. Please try again.");
    } finally {
      setIsDealTransferring(false);
    }
  };

  const allSelected = openDeals.length > 0 && selectedDeals.length === openDeals.length;

  if (!open) return null;

  return (
    <div className={classes.backdrop} onClick={onClose}>
      <div className={classes.dialog} onClick={(e) => e.stopPropagation()}>
        <div className={classes.dialogTitle}>
          <div className={classes.titleHeader}>
            <div className={classes.iconWrapper}>
              <Person style={{ color: "#445af3" }} />
            </div>
            <h2 className={classes.title}>Transfer Open Deals</h2>
          </div>
        </div>

        <div className={classes.divider} />

        <div style={{ padding: "0 24px", flex: 1, display: "flex", flexDirection: "column", marginBottom: 20 }}>
          <div className={classes.transferHeader}>
            <div className={classes.transferFlow}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div className={classes.transferDot} style={{ backgroundColor: "#445af3" }} />
                <p className={classes.transferName}>{usersObj[leadOwnerDetails?.previousOwnerId]?.full_name || ""}</p>
              </div>
              <div className={classes.transferLine} />
              <p className={classes.transferText}>transferring to</p>
              <div className={classes.transferLine} />
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <p className={classes.transferName}>{usersObj[leadOwnerDetails?.newOwnerId]?.full_name || ""}</p>
                <div className={classes.transferDot} style={{ backgroundColor: "#388e3c" }} />
              </div>
            </div>
            <p className={classes.transferText}>
              The contact you reassigned has{" "}
              <strong>
                {openDeals.length} open deal{openDeals.length !== 1 ? "s" : ""}
              </strong>
              .To transfer the deal{openDeals.length !== 1 ? "s" : ""} to the new lead owner, select them below.
              If you don’t want to transfer the deals, click cancel.
            </p>
          </div>
          <div className={classes.dialogContent}>
            <div className={classes.selectAllRow}>
              <div className={classes.checkboxLabel}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allSelected}
                      indeterminate={selectedDeals.length > 0 && selectedDeals.length < openDeals.length}
                      onChange={handleSelectAll}
                      color='primary'
                      style={{ color: "#445af3" }}
                    />
                  }
                  label={`Select all deals (${openDeals.length})`}
                  classes={{ label: classes.checkboxLabel }}
                />
              </div>
              <div className={classes.chip}>
                {selectedDeals.length} of {openDeals.length} selected
              </div>
            </div>

            <div className={classes.divider} />

            <div className={classes.dealsContainer}>
              {openDeals.map((deal) => {
                const isChecked = selectedDeals.includes(deal.contact_stage_id);
                return (
                  <div
                    key={deal.contact_stage_id}
                    style={{ padding: "16px" }}
                    className={`${classes.dealCard} ${isChecked ? "selected" : ""}`}
                    onClick={() => handleSelectDeal(deal.contact_stage_id)}
                  >
                    <div
                      className={isChecked ? "checked" : ""}
                      style={{
                        margin: "4px 0",
                        marginLeft: "-12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <FormControlLabel
                        onClick={(e) => e.stopPropagation()}
                        control={
                          <Checkbox
                            key={deal.id}
                            checked={isChecked}
                            color='primary'
                            style={{ color: "#445af3" }}
                            onChange={() => handleSelectDeal(deal.contact_stage_id)}
                          />
                        }
                        label={
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                              flexWrap: "wrap",
                            }}
                          >
                            <div style={{ width: "300px", display: "flex", alignItems: "center" }}>
                              <Typography
                                component='span'
                                variant='subtitle2'
                                style={{ color: "var(--dark_blue)", width: "150px", fontSize: "15px" }}
                              >
                                <b>Deal Name: </b>
                              </Typography>
                              <Typography
                                component='span'
                                variant='body2'
                                style={{
                                  width: "100%",
                                  whiteSpace: "normal",
                                  overflow: "hidden",
                                  wordBreak: "break-word",
                                  fontSize: "14px",
                                }}
                                color='textSecondary'
                              >
                                {deal?.deal_title || "No title"}
                              </Typography>
                            </div>
                          </div>
                        }
                        style={{ margin: 0 }}
                      />

                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <Typography
                          component='span'
                          variant='subtitle2'
                          style={{ color: "var(--dark_blue)", height: "unset", fontSize: "15px" }}
                        >
                          <b>Premium: </b>
                        </Typography>
                        <Typography
                          style={{
                            height: "unset",
                            whiteSpace: "normal",
                            overflow: "hidden",
                            wordBreak: "break-all",
                            fontSize: "14px",
                          }}
                          component='span'
                          variant='body2'
                          color='textSecondary'
                        >
                          {Utils.calculateCash(deal?.deal_value)}
                        </Typography>
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        marginBottom: "16px",
                        flexWrap: "wrap",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <div style={{ width: "45%", display: "flex", alignItems: "center", gap: 4 }}>
                          <Typography
                            component='span'
                            variant='subtitle2'
                            style={{ color: "var(--dark_blue)", height: "unset", fontSize: "15px" }}
                          >
                            <b>Pipeline: </b>
                          </Typography>
                          <Typography
                            style={{
                              height: "unset",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              fontSize: "14px",
                              border: "1px solid #e0e0e0",
                              padding: "4px 8px",
                              borderRadius: "4px",
                              flex: 1,
                            }}
                            component='span'
                            variant='body2'
                            color='textSecondary'
                          >
                            {deal?.pipeline_title || "N/A"}
                          </Typography>
                        </div>
                        <div style={{ width: "45%", display: "flex", alignItems: "center", gap: 4 }}>
                          <Typography
                            component='span'
                            variant='subtitle2'
                            style={{ color: "var(--dark_blue)", height: "unset", fontSize: "15px" }}
                          >
                            <b>Stage: </b>
                          </Typography>
                          <Typography
                            style={{
                              height: "unset",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              fontSize: "14px",
                              border: "1px solid #e0e0e0",
                              padding: "4px 8px",
                              borderRadius: "4px",
                              flex: 1,
                            }}
                            component='span'
                            variant='body2'
                            color='textSecondary'
                          >
                            {deal?.stage_title || "N/A"}
                          </Typography>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className={classes.dialogActions}>
          <div>
            {selectedDeals.length > 0 && (
              <div className={classes.selectedInfo}>
                <CheckCircle fontSize='small' style={{ color: "#445af3" }} />
                <p className={classes.selectedText}>
                  {selectedDeals.length} deal{selectedDeals.length !== 1 ? "s" : ""} will be transferred
                </p>
              </div>
            )}
          </div>

          <div className={classes.buttonGroup}>
            <button
              className={`${classes.button} ${classes.buttonOutlined}`}
              onClick={() => {
                onClose();
              }}
            >
              Cancel
            </button>
            <button
              disabled={selectedDeals.length === 0 || isDealTransfering}
              className={`${classes.button}  ${classes.buttonContained}`}
              onClick={handleTransfer}
            >
              {isDealTransfering ? "Transferring..." : "Transfer"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DealTransferDialog;
